import { aeriosPx } from "./aerios.gen.js";

/**
 * D72 — one radius scale, four steps, applied by what a thing IS.
 *
 * Square-by-default is retired and the SQUARED corner became the accent — the
 * exact inversion of what D11 held. `--radius-surface` and `--arc` no longer
 * exist; a build still setting them is on v2.0.0 rules.
 *
 * **All radii are fixed px. Percentage radii are banned outright**, and that
 * ban is what closes the `--arc` defect rather than patching it: `0 0 100% 0`
 * resolves per-axis, so on any non-square frame it draws a stretched ellipse
 * instead of the quarter-circle it looks like. Two independent builds hit that,
 * and the website's 16:9 workaround had to be recomputed for every aspect
 * ratio. A fixed px corner is correct at every ratio, so the class of bug
 * disappears. `pill`'s 999 is a sentinel meaning "fully round", not an
 * exception — and `no-raw-design-values` fails the build on a `%` radius.
 *
 * What this cost the platform, per D72's own migration note: controls and media
 * were already at 12, so they need no change; cards move 18 -> 16 and pills
 * 980 -> 999. `ANALYSIS_PANEL_RADIUS` was a third card value at 10px, chosen to
 * sit between the utility and card radii — D72 names it explicitly and it goes.
 *
 * The legacy `sm` / `base` / `md` / `lg` / `2xl` keys are kept so no consumer
 * has to change in the same commit, but they now resolve to the four real
 * steps. There is no 6px and no 24px any more: if two names point at one value,
 * that is the scale telling you it only has four.
 */
export const radius = {
  none: 0,

  /** button · input · select · textarea · boxed chip */
  control: aeriosPx.radiusControl, // 12
  /** media frame · thumbnail · scan and map viewport */
  media: aeriosPx.radiusMedia, // 12
  /** card · panel · popover · sheet · nav card */
  card: aeriosPx.radiusCard, // 16
  /** pill · tag · avatar · toggle track. A sentinel, not a measurement. */
  pill: aeriosPx.radiusPill, // 999

  // ---- legacy names, resolved onto the four steps above ----
  sm: aeriosPx.radiusControl,
  base: aeriosPx.radiusControl,
  md: aeriosPx.radiusControl,
  lg: aeriosPx.radiusCard,
  "2xl": aeriosPx.radiusCard,
} as const;

/**
 * The accent corner (D72) — ONE squared corner on a rounded element.
 *
 * An opt-in modifier, never a card default, and it inherits the budget the arc
 * held: **one per page**, on the same three permitted hosts (hero media frame,
 * feature card, diagram terminus).
 *
 * **Plain radius on repeating data.** Anything rendered from a collection —
 * dashboard card grids, table rows, list items, result cards, filter chips —
 * takes `card` plain on all four corners and never this. The test is
 * mechanical: *if two of it can appear side by side, it does not get the
 * corner.* Eight cards each carrying the signature is eight times it signifies
 * nothing.
 *
 * Which, in a product that is almost entirely repeating data, means this is
 * exported to be findable rather than because much can use it.
 */
export const cornerAccent = "16px 16px 16px 0" as const;
